var _a;
const u = up.util;
up.Change.FromResponse = (_a = class FromResponse extends up.Change {
        constructor(options) {
            super(options);
            this._response = options.response;
            this._request = this._response.request;
        }
        execute() {
            if (up.fragment.config.skipResponse(this._loadedEventProps())) {
                this._skip();
            }
            else {
                this._request.assertEmitted('up:fragment:loaded', {
                    ...this._loadedEventProps(),
                    callback: this.options.onLoaded,
                    log: ['Loaded fragment from %s', this._response.description],
                    skip: () => this._skip()
                });
                this._assetRenderableResponse();
            }
            let fail = u.evalOption(this.options.fail, this._response) ?? !this._response.ok;
            if (fail) {
                throw this._updateContentFromResponse(this.deriveFailOptions());
            }
            return this._updateContentFromResponse(this.options);
        }
        _assetRenderableResponse() {
            if (!up.fragment.config.renderableResponse(this._response)) {
                throw new up.CannotParse(['Cannot render response with content-type "%s" (configure with up.fragment.config.renderableResponse)', this._response.contentType]);
            }
        }
        _skip() {
            up.puts('up.render()', 'Skipping ' + this._response.description);
            this.options.target = ':none';
            this.options.failTarget = ':none';
        }
        _updateContentFromResponse(finalRenderOptions) {
            if (finalRenderOptions.didForceFailOptions) {
                up.puts('up.render()', 'Rendering failed response using fail-prefixed options (https://unpoly.com/failed-responses)');
            }
            this._augmentOptionsFromResponse(finalRenderOptions);
            finalRenderOptions.meta = this._helloMeta();
            finalRenderOptions.verifyFinished = (result) => this._revalidate(result, finalRenderOptions);
            return new up.Change.FromContent(finalRenderOptions).execute();
        }
        async _revalidate(renderResult, originalRenderOptions) {
            if (!up.fragment.shouldRevalidate(this._request, this._response, originalRenderOptions))
                return;
            let inputTarget = originalRenderOptions.target;
            let effectiveTarget = renderResult.target;
            if (/:(before|after)/.test(inputTarget)) {
                up.warn('up.render()', 'Cannot revalidate cache when prepending/appending (target %s)', inputTarget);
            }
            else {
                up.puts('up.render()', 'Revalidating cached response for target "%s"', effectiveTarget);
                let revalidateResult = await up.reload(effectiveTarget, {
                    ...originalRenderOptions,
                    ...up.RenderOptions.NO_MOTION,
                    ...up.RenderOptions.NO_INPUT_INTERFERENCE,
                    ...up.RenderOptions.NO_PREVIEWS,
                    feedback: originalRenderOptions.feedback,
                    preferOldElements: renderResult.fragments,
                    layer: renderResult.layer,
                    onFinished: null,
                    expiredResponse: this._response,
                    preview: this._revalidatePreview(originalRenderOptions),
                    abort: false,
                    cache: false,
                    background: true,
                });
                if (!revalidateResult.none) {
                    return revalidateResult;
                }
            }
        }
        _revalidatePreview({ preview, revalidatePreview }) {
            if (revalidatePreview === true) {
                return preview;
            }
            else {
                return revalidatePreview;
            }
        }
        _loadedEventProps() {
            const { expiredResponse } = this.options;
            return {
                request: this._request,
                response: this._response,
                renderOptions: this.options,
                revalidating: !!expiredResponse,
                expiredResponse,
            };
        }
        _helloMeta() {
            let meta = { revalidating: !!this.options.expiredResponse, ok: this._response.ok };
            up.migrate.processHelloMeta?.(meta, this._response);
            return meta;
        }
        _augmentOptionsFromResponse(renderOptions) {
            const responseURL = this._response.url;
            let serverLocation = responseURL;
            let hash = this._request.hash;
            if (hash) {
                renderOptions.hash = hash;
                serverLocation += hash;
            }
            const isReloadable = (this._response.method === 'GET');
            if (isReloadable) {
                renderOptions.source = up.history.refineOption(renderOptions.source, responseURL);
            }
            else {
                renderOptions.source = up.history.refineOption(renderOptions.source, 'keep');
                renderOptions.history = !!renderOptions.location;
            }
            let openLayerOptions = this._response.openLayer;
            if (openLayerOptions) {
                up.script.adoptRenderOptionsFromHeader(openLayerOptions, this._response.cspInfo);
                Object.assign(renderOptions, {
                    ...up.Layer.Overlay.UNSET_VISUALS,
                    target: undefined,
                    ...up.fragment.config.navigateOptions,
                    ...openLayerOptions,
                    layer: 'new',
                });
            }
            renderOptions.location = up.history.refineOption(renderOptions.location, serverLocation);
            renderOptions.title = up.history.refineOption(renderOptions.title, this._response.title);
            renderOptions.eventPlans = this._response.eventPlans;
            let serverTarget = this._response.target;
            if (serverTarget) {
                renderOptions.target = serverTarget;
            }
            renderOptions.acceptLayer = this._response.acceptLayer;
            renderOptions.dismissLayer = this._response.dismissLayer;
            renderOptions.document = this._response.text;
            if (this._response.none) {
                renderOptions.target = ':none';
            }
            renderOptions.context = u.merge(renderOptions.context, this._response.context);
            renderOptions.cspInfo = this._response.cspInfo;
            renderOptions.time ??= this._response.lastModified;
            renderOptions.etag ??= this._response.etag;
        }
    },
    (() => {
        u.memoizeMethod(_a.prototype, {
            _loadedEventProps: true,
        });
    })(),
    _a);
